import { Component, OnInit } from '@angular/core';
import * as d3 from 'd3';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { urlEndPoint } from 'src/app/common';

import { Message } from 'src/app/models/message';
import { Tier3Relations } from 'src/app/models/obs/Tier3Relations';
import { BackendResponse, ResponseOneTier3, ResponsePeriod, ResponseQuadrant, ResponseTematic, ResponseTier1, ResponseTier3, ResponseTier3Relations } from 'src/app/models/obs/apiResponse';
import { SignalPeriod } from 'src/app/models/obs/signalPeriod';
import { SignalQuadrant } from 'src/app/models/obs/signalQuadrant';
import { Tematic } from 'src/app/models/obs/tematic';
import { Tier1 } from 'src/app/models/obs/tier1';

import { Tier3 } from 'src/app/models/obs/tier3';
import { SignalPeriodService } from 'src/app/services/obs/signal-period.service';
import { SignalQuadrantService } from 'src/app/services/obs/signal-quadrant.service';
import { TematicService } from 'src/app/services/obs/tematic.service';
import { Tier1Service } from 'src/app/services/obs/tier1.service';
import { Tier3RelationsService } from 'src/app/services/obs/tier3-relations.service';
import { Tier3Service } from 'src/app/services/obs/tier3.service';
import { ErrorHandlingService } from 'src/app/services/util/error-handling.service';

@Component({
  selector: 'app-radar',
  templateUrl: './radar.component.html',
  styleUrls: ['./radar.component.scss']
})
export class RadarComponent implements OnInit {

  tematics: Tematic[] = [];
  periods: SignalPeriod[] = [];
  quadrants: SignalQuadrant[] = [];
  tier3s: Tier3[] = [];
  tier1s: Tier1[] = [];
  tier3Relations: Tier3Relations[] = [];
  riskRelations: Tier3Relations[] = [];
  oppRelations: Tier3Relations[] = [];

  URL: string = urlEndPoint;

  msg: Message;
  selectedNode: Tier3 = null;
  selectedNode2: Tier3 = {
    "id": 176,
    "idtier1": 8,
    "idtematic": 4,
    "angle": 15,
    "marginx": 0,
    "marginy": -5,
    "code": "s1",
    "version": 1,
    "shorttitle": "Nuevas modalidades de trabajo",
    "longtitle": "Adopción de nuevas modalidades de trabajo",
    "summary": "Al principio del estado de emergencia, se dispuso el confinamiento obligatorio de la población para 15 días con el propósito de aumentar el distanciamiento social y así reducir la cantidad de contagiados por COVID-19, entendiéndose que éste acabaría a fines del mes de marzo. No obstante, el confinamiento se prorrogó múltiples veces hasta el 30 de junio del 2020 lo cual implicó la paralización de la mayoría de las actividades económicas por más de tres meses. Esta situación fue especialmente preocupante para la población ocupada informal que viven del comercio y otras actividades que tienen ingresos diarios. ",
    "detail": "La extensión del confinamiento por la expansión del COVID podría tener diversas implicancias negativas, como el aumento de la delincuencia y una profunda inestabilidad social generados por la desesperación de la población por ingresos y el manejo ineficiente de los recursos por parte del gobierno durante la pandemia de la COVID-19. La ocurrencia de estos eventos futuros generaría el incremento de actos delictivos y movimiento sociales violentos que afectarán las libertades de la población y las dinámicas económicas. ",
    "topdata": "Prorrogación repetida de la cuarentena para el control de la expansión de la pandemia de la COVID-19",
    "img": null,
    "urlfile": "assets/img/s/s1.jpg",
    "visible": true,
    "iddpto": null,
    "idsector": null,
    "status": false,
    "tags": null,
    "idperiod": 1,
    "idquadrant": 1,
    "drivers": "Confinamiento y cuarentenas: superación de barreras culturales y tecnológicas, conectividad digital",
    "suppositions": "<ul><li>Durante el confinamiento por la pandemia, los trabajos digitales y basados en el conocimiento tuvieron más probabilidad de trabajar de forma remota.</li><li>Preferencia por el trabajo completamente remoto por países en desarrollo.</li></ul>",
    "viewst3": 0
  };

  sortOrder = {
    column: '',
    direction: '',
  };

  constructor(
    private tier3Service: Tier3Service,
    private tier3RelationsService: Tier3RelationsService,
    private periodService: SignalPeriodService,
    private quadrantService: SignalQuadrantService,
    private tematicService: TematicService,
    private tier1Service: Tier1Service,
    private errorService: ErrorHandlingService
  ) {

  }

  ngOnInit() {
    forkJoin([
      this.getTematics(),
      this.getPeriods(),
      this.getQuadrants(),
      this.getTier1ByEventType(5),
      this.getTier3Signals(5)
    ]).subscribe(results => {
      this.tematics = results[0]
      this.periods = results[1]
      this.quadrants = results[2]
      this.tier1s = results[3]
      this.tier3s = results[4];
      // Llama a createChart una vez que se hayan completado las llamadas anteriores
      this.createChart();
    });
  }

  getTier3Signals(id: number): Observable<Tier3[]> {
    return this.tier3Service.get4Event(id).pipe(
      map((res: BackendResponse<Tier3[]>) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          throw new Error(res.message);
        }
      }),
      catchError(err => {
        this.msg = this.errorService.handleError(err);
        return of(null);
      })
    );
  }

  getPeriods(): Observable<SignalPeriod[]> {
    return this.periodService.getAll().pipe(
      map((res: BackendResponse<SignalPeriod[]>) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          throw new Error(res.message);
        }
      }),
      catchError(err => {
        this.msg = this.errorService.handleError(err);
        return of(null);
      })
    );
  }

  getQuadrants(): Observable<ResponseQuadrant> {
    return this.quadrantService.getAll().pipe(
      map((res: BackendResponse<ResponseQuadrant>) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          throw new Error(res.message);
        }
      }),
      catchError(err => {
        this.msg = this.errorService.handleError(err);
        return of(null); // Devolver un observable nulo si hay un error
      })
    );
  }

  getTematics(): Observable<ResponseTematic> {
    return this.tematicService.getAll().pipe(
      map((res: BackendResponse<ResponseTematic>) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          throw new Error(res.message);
        }
      }),
      catchError(err => {
        this.msg = this.errorService.handleError(err);
        return of(null); // Devolver un observable nulo si hay un error
      })
    );
  }

  /*   Probable no usado
      getTier1ByidTier1(id: number) {
      return this.tier1Service.getOne(id).subscribe(
        ({ data }: ResponseTier1) => { this.tier1s = data; },
        err => { this.msg = this.errorService.handleError(err); }
      );
    }
   */
  getTier1ByEventType(id: number): Observable<Tier3[]> {
    return this.tier1Service.getByEvent(id).pipe(
      map((res: BackendResponse<Tier3[]>) => {
        if (res.status === 'OK') {
          return res.data;
        } else {
          throw new Error(res.message);
        }
      }),
      catchError(err => {
        this.msg = this.errorService.handleError(err);
        return of(null); // Devolver un observable nulo si hay un error
      })
    );
  }

  getTier3RelationsByIdTier3(id: number) {
    this.riskRelations = [];
    this.oppRelations = [];
    this.tier3RelationsService.getByTier3(id).subscribe({
      next: ({ data }: ResponseTier3Relations) => {
        const observablesArray: Observable<Tier3>[] = [];

        data.forEach(risk => {
          observablesArray.push(this.getLongTitleOfTier3ById(risk.idtier3related));
        });

        forkJoin(observablesArray).subscribe(modifiedTier3 => {
          this.riskRelations = modifiedTier3.filter(relation => relation.idtier1 === 6);
          this.oppRelations = modifiedTier3.filter(relation => relation.idtier1 === 4);
        });

      },
      error: err => { this.msg = this.errorService.handleError(err); }
    });
  }

  getLongTitleOfTier3ById(id: number): Observable<Tier3> {
    return this.tier3Service.getOne(id).pipe(
      map(({ data }: ResponseOneTier3) => {
        return {
          ...data,
          shorttitle: data.longtitle
        };
      }),
      catchError(err => {
        this.msg = this.errorService.handleError(err);
        return throwError(err);
      })
    );
  }

  getOneTier3ById(id: number) {
    this.tier3Service.getOne(id).subscribe(
      async ({ data }: ResponseOneTier3) => {
        this.selectedNode = data;
        if (this.selectedNode) {
          this.quadrants.forEach(q => {
            if (q.id == this.selectedNode.idquadrant) {
              this.selectedNode.quadrant = q.shorttitle;
            }
          });
          this.periods.forEach(p => {
            if (p.id == this.selectedNode.idperiod) {
              this.selectedNode.period = p.shorttitle;
            }
          });
          this.tier1s.forEach(t1 => {
            if (t1.id == this.selectedNode.idtier1) {
              this.selectedNode.tier1 = t1.shorttitle;
            }
          });
        }
      },
      err => { this.msg = this.errorService.handleError(err); }
    );
    this.getTier3RelationsByIdTier3(id);
  }

  clicked(tier3: Tier3) {
    this.getOneTier3ById(tier3.id);
  }

  createChart(): void {

    var self = this;

    // Establecer dimensiones del gráfico
    const margin = { top: 60, right: 60, bottom: 60, left: 60 };
    const width = 1000 - margin.left - margin.right;
    const height = 1000 - margin.top - margin.bottom;
    const radius = Math.min(width, height) / 2;

    // Crear el objeto svg en el contenedor del gráfico
    const svg = d3.select("#my_dataviz")
      .append("svg")
      .attr("viewBox", `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .append("g")
      .attr("transform", `translate(${width / 2 + margin.left}, ${height / 2 + margin.top})`);


    // Determinar los radios de los círculos de forma proporcional
    var circleData = [];
    for (var i = 1; i <= self.periods.length; i++) {
      var radio_circle;
      if (i == 1) {
        radio_circle = 260;
      } else if (i == 2) {
        radio_circle = 410;
      } else {
        radio_circle = i * 0.4 * radius;
      }
      circleData.unshift(radio_circle);
    }

    var radios: number[] = []; // Arreglo para almacenar las radios
    circleData.forEach(function (element, index) {
      var radio = element - margin.top; // Calcula el valor de la radio
      radios.unshift(radio); // Agrega la radio al arreglo
    });
    //Determinamos el radio maximo
    var Rmaximo = Math.max(...radios);

    //Se crean los circulos
    var colors = ['#F2F3F4', '#E5E8E8', '#D5D8DC']; // Arreglo de colores en el orden original
    var colorScale = d3.scaleOrdinal()
      .domain(circleData.map(function (d, i) { return i.toString(); }))
      .range(colors.slice().reverse()); // Invertimos el orden de los colores

    svg.selectAll('circle')
      .data(circleData)
      .enter()
      .append('circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', function (d) {
        return d - margin.top; // ajustamos el radio restando el margen
      })
      .attr('fill', function (d, i) {
        return colorScale(i.toString()) as string; // aseguramos que el valor sea de tipo string
      })
      .attr('stroke', 'transparent')
      .attr('stroke-width', '1px');
    //añadimos el eje horizontal
    svg.append("line")
      .attr("x1", -circleData[0])
      .attr("x2", circleData[0])
      .attr("y1", 0)
      .attr("y2", 0)
      .attr("stroke", "white")
      .attr("stroke-width", "4px");

    // añadimos el eje vertical
    svg.append("line")
      .attr("y1", -circleData[0])
      .attr("y2", circleData[0])
      .attr("x1", 0)
      .attr("x2", 0)
      .attr("stroke", "white")
      .attr("stroke-width", "4px");

    //Función para posicionar los periodos
    function pos_periodo(d: any, i: any) {
      var xValue = 0;
      var info_periodos = [];
      for (var j = 0; j < self.periods.length; j++) {
        if (d.id === self.periods[j].id) { // Comparar d.id con el valor de la propiedad 'id' en self.periods[j]
          if (j === 0) {
            xValue = radios[j] / 2;
          } else {
            xValue = (radios[j] + radios[j - 1]) / 2;
          }
          info_periodos.push(self.periods[j]); // Agregar el objeto coincidente a id_periodos
          break;
        }
      }
      return xValue;
    }

    //añadimos los periodos al grafico
    svg.selectAll('.year')
      .data(self.periods, (d: any) => { return d.shorttitle; })
      .enter()
      .append('text')
      .attr('class', 'year')
      .attr('x', pos_periodo)
      .attr('y', -10)
      .attr('text-anchor', 'middle')
      .attr('alignment-baseline', 'middle')
      .text((d) => { return d.shorttitle; })
      .style('font-size', '20px')
      .style('font-weight', 'bold')
      .style('fill', 'black')
      .style("font-family", "Roboto,sans-serif")
      .style("font-style", "normal");

    //Calculamos las posiciones de los eventos
    function calcularPosicion(r: any, e: any) {
      var x: any, y: any;
      var angleInRadians = e.angle * Math.PI / 180;
      switch (e.idquadrant) {
        case 1:
          x = r * Math.abs(Math.cos(angleInRadians)) + parseInt(e.marginx);
          y = -r * Math.abs(Math.sin(angleInRadians)) + parseInt(e.marginy);
          break;
        case 2:
          x = -r * Math.abs(Math.cos(angleInRadians)) + parseInt(e.marginx);
          y = -r * Math.abs(Math.sin(angleInRadians)) + parseInt(e.marginy);
          break;
        case 3:
          x = -r * Math.abs(Math.cos(angleInRadians)) + parseInt(e.marginx);
          y = r * Math.abs(Math.sin(angleInRadians)) + parseInt(e.marginy);
          break;
        case 4:
          x = r * Math.abs(Math.cos(angleInRadians)) + parseInt(e.marginx);
          y = r * Math.abs(Math.sin(angleInRadians)) + parseInt(e.marginy);
          break;
        default:
          return;
      }

      //Funciones para posicionar los nombres de los eventos en X,Y
      function pos_title_x() {
        return x + parseInt(e.margintx);
      }
      function pos_title_y() {
        return y + parseInt(e.marginty);
      }

      //Agrega los nombres de los eventos
      var text = svg.append("text")
        .attr("class", "point-label")
        .attr("id", "event-title-" + e.id)
        .attr("x", pos_title_x)
        .attr("y", pos_title_y)
        .style("font-size", 13 + "px")
        .style("font-family", "Roboto,sans-serif")
        .style("font-style", "normal")
        .style("text-anchor", "middle")
        .style("cursor", "pointer")
        .on("click", (e) => handleClick())
        .on("mouseover", handleMouseOver)
        .on("mouseout", handleMouseOut)
        .text(e.shorttitle);
      // SALTOS DE LÍNEA
      var characters_line = 22; // Cantidad de caracteres por línea
      var words = text.text().split(' ');
      var lines = [];
      var current_line = '';
      words.forEach(function (word) {
        // Calcular la longitud sin contar los espacios en blanco
        var word_length = word.replace(/\s/g, '').length;

        if (current_line.length + word_length <= characters_line) {
          current_line += word + ' ';
        } else {
          lines.push(current_line.trim());
          current_line = word + ' ';
        }
      });
      lines.push(current_line.trim());
      if (e.idperiod != 1) {
        text.text(null); // Eliminar el texto original
        lines.forEach(function (line, index) {
          text.append("tspan")
            .text(line)
            .attr("x", pos_title_x)
            .attr("dy", index > 0 ? "13" : "0");
        });
      }

      // Agregamos los íconos
      var iconSize = 36;
      var fontSize = 13;
      function handleClick() {
        self.clicked(e);
      }
      //Eventos click,mouseOver
      function handleMouseOver() {
        iconSize += 4;
        iconSvg.attr('width', iconSize)
          .attr('height', iconSize);
        fontSize += 4;
        text.style("font-size", fontSize + "px")
          .style("font-weight", "bold")
      }
      function handleMouseOut() {
        iconSize -= 4;
        iconSvg.attr('width', iconSize)
          .attr('height', iconSize);
        fontSize -= 4;
        text.style("font-size", fontSize + "px")
          .style("font-weight", "")
      }
      //Funcion para definir el tipo de icono
      function icon_evento(idtier1: any) {
        if (e.idtier1 == 8) {
          return 'M12.001 3C16.2854 3 20.2201 4.49683 23.3108 6.99607L22.4994 8H18.001L18.0004 13.571L12.001 21L0.69043 6.99671C3.78127 4.49709 7.71632 3 12.001 3ZM22.001 19V21H20.001V19H22.001ZM22.001 10V17H20.001V10H22.001Z';
        } else if (e.idtier1 == 18) {
          return 'M20,0H4C3.4,0,3,0.4,3,1v22c0,0.6,0.4,1,1,1h16c0.6,0,1-0.4,1-1V1C21,0.4,20.6,0,20,0z M19,22H5V2h14V22z M11,15v2h2v-2c2,0,3-1.1,3-3c0-2.2-2-3-4-6c-2,3-4,3.8-4,6C8,13.9,9,15,11,15z';
        } else if (e.idtier1 == 24) {
          return 'M6 18H18V6H6V18ZM14 20H10V22H8V20H5C4.44772 20 4 19.5523 4 19V16H2V14H4V10H2V8H4V5C4 4.44772 4.44772 4 5 4H8V2H10V4H14V2H16V4H19C19.5523 4 20 4.44772 20 5V8H22V10H20V14H22V16H20V19C20 19.5523 19.5523 20 19 20H16V22H14V20ZM8 8H16V16H8V8Z';
        }
        return '';
      }
      //Funcion para definir los colores de los eventos
      function color_evento() {
        // Obtener el objeto tematica correspondiente al idtematic del evento
        var tema = self.tematics.find(function (tema) {
          return tema.id === e.idtematic;
        });
        // Verificar si se encontró el tema y devolver su color, o un color predeterminado en caso contrario
        if (tema) {
          return tema.color;
        } else {
          return '#000000'; // Color predeterminado en caso de que no se encuentre el tema
        }
      }

      var iconGroup = svg.append('g')
        .attr('transform', 'translate(' + x + ',' + y + ')');
      var iconSvg = iconGroup.append('svg')
        .attr('width', 36)
        .attr('height', 36)
        .attr('viewBox', '0 0 36 36')
        .style("cursor", "pointer")
        .on("click", (e) => handleClick())
        .on("mouseover", handleMouseOver)
        .on("mouseout", handleMouseOut);
      iconSvg.append('path')
        .attr('d', icon_evento(e.idtier1))
        .attr('fill', color_evento);
      iconGroup.append('rect')
        .attr('width', 30)
        .attr('height', 30)
        .attr('fill', 'transparent')
        .style("cursor", "pointer")
        .on("click", (e) => handleClick())
        .on("mouseover", handleMouseOver)
        .on("mouseout", handleMouseOut);
    }

    // Ubicar los objetivos en los cuadrantes
    let cuadrantesProcesados: any = [];
    for (let i = 0; i < self.quadrants.length; i++) {
      const objetivo = self.quadrants[i];
      // Verificar si el cuadrante ya tiene asignado un objetivo
      if (cuadrantesProcesados.includes(objetivo.id)) {
        continue;
      }
      // Agregar el cuadrante a la lista de procesados
      cuadrantesProcesados.push(objetivo.id);
      var pathId = "textcircle" + objetivo.id;
      var pathD;
      var startOffset;
      var textAnchor;

      switch (objetivo.id) {
        case 1:
          pathD = "M0," + (Rmaximo - 10) + "a" + (Rmaximo + 10) + "," + (Rmaximo + 10) + " 0 0,1 0,-" + (Rmaximo + 10) * 2 + " a" + (Rmaximo + 10) + "," + (Rmaximo + 10) + " 0 0,1 0," + (Rmaximo + 10) * 2 + " Z";
          startOffset = "66%";
          textAnchor = "middle";
          break;
        case 2:
          pathD = "M0," + (Rmaximo - 10) + "a" + (Rmaximo + 10) + "," + (Rmaximo + 10) + " 0 0,1 0,-" + (Rmaximo + 10) * 2 + " a" + (Rmaximo + 10) + "," + (Rmaximo + 10) + " 0 0,1 0," + (Rmaximo + 10) * 2 + " Z";
          startOffset = "37%";
          textAnchor = "middle";
          break;
        case 3:
          pathD = "M0," + (Rmaximo + 32) + "a" + (Rmaximo + 42) + "," + (Rmaximo + 42) + " 0 0,0 0,-" + (Rmaximo + 42) * 2 + " a" + (Rmaximo + 42) + "," + (Rmaximo + 42) + " 0 0,0 0," + (Rmaximo + 42) * 2 + " Z";
          startOffset = "85%";
          textAnchor = "middle";
          break;
        case 4:
          pathD = "M0," + (Rmaximo + 32) + "a" + (Rmaximo + 42) + "," + (Rmaximo + 42) + " 0 0,0 0,-" + (Rmaximo + 42) * 2 + " a" + (Rmaximo + 42) + "," + (Rmaximo + 42) + " 0 0,0 0," + (Rmaximo + 42) * 2 + " Z";
          startOffset = "12%";
          textAnchor = "middle";
          break;
        default:
          continue; // Saltar a la siguiente iteración del bucle
      }
      var path = svg
        .append("defs")
        .append("path")
        .attr("id", pathId)
        .attr("d", pathD);

      var text = svg
        .append("text")
        .append("textPath")
        .attr("xlink:href", "#" + pathId)
        .attr("aria-label", objetivo.shorttitle)
        .style("font-size", 28 + "px")
        .style("font-family", "Roboto, sans-serif")
        .style("font-weight", "bold")
        .style("font-style", "normal")
        .text(objetivo.shorttitle)
        .attr("startOffset", startOffset)
        .attr("text-anchor", textAnchor);
    }
    //Llamado a las funcion para ubicar a los eventos en el grafico
    for (var i = 0; i < self.tier3s.length; i++) {
      var evento = self.tier3s[i];
      var id_encontrado = false; // Variable para rastrear si se encuentra una coincidencia
      for (let index = 0; index < self.periods.length; index++) {
        var element = self.periods[index];
        if (evento.idperiod == element.id) {
          calcularPosicion(radios[index] - 50, evento);
          id_encontrado = true;
          break; // Salir del bucle interno si se encuentra una coincidencia
        }
      }
    }
  }

  closeModal() {
    this.selectedNode = null;
  }

  onHeaderClick(column: string, data: any) {
    if (this.sortOrder.column === column) {
      this.sortOrder.direction = this.sortOrder.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortOrder.column = column;
      this.sortOrder.direction = 'asc';
    }

    this.sortData(data);
  }

  sortData(data: any) {
    data.sort((a, b) => {
      const columnValueA = a[this.sortOrder.column];
      const columnValueB = b[this.sortOrder.column];

      if (typeof columnValueA === 'string') {
        return this.sortOrder.direction === 'asc'
          ? columnValueA.localeCompare(columnValueB)
          : columnValueB.localeCompare(columnValueA);
      } else {
        return this.sortOrder.direction === 'asc'
          ? columnValueA - columnValueB
          : columnValueB - columnValueA;
      }
    });
  }
}
